import { apiConfig } from "@grindex-client/common/js/helpers/apiConfig";
import { keyCodes } from "@grindex-client/common/js/helpers/constants";
import { debounce } from "@grindex-client/common/js/helpers/debounce";
import {
	HDR_ACTIVATION_POINT,
	SCROLL_LIMIT,
	closeMobileNavigation,
	openMobileNavigation,
} from "@grindex-client/common/js/helpers/navigationHelper";
import { underscoreTemplateGrindex } from "@grindex-client/common/js/helpers/underscore-helper";
import { xylemStorage } from "@grindex-client/components/SessionStorage/xylemStorage";
import { addListener, getNextSibling, getPreviousSibling, height } from "@grindex-common/helpers/domManipulation";

/**
 *
 * @param {NodeListOf<HTMLElement>} $views
 */
function init([$view]) {
	const $module = $view;

	xylemHeader($module);
}

/**
 * @param {HTMLElement} $module
 */
function xylemHeader($module) {
	const CLASS_ICON_CLOSE = "icon-close-lg";
	const CLASS_ICON_MENU_TRIGGER = "icon-menu-lg";
	const CLASS_ICON_SEARCH_TRIGGER = "icon-search-new";

	const ELEMENT_PREDICTION_TRIANGLE = "<div class='triangle'></div>";

	const $searchClearBtn = $module.querySelectorAll(".clear-btn");
	const $searchTriggers = $module.querySelectorAll(".search-trigger");

	const $predictions = $module.querySelector(".c-predictive-search .predictive-search-container");
	const $searchForm = $module.querySelector("li.c-predictive-search.main-search form");
	const $mainSearchContainer = $module.querySelector(".main-search.c-predictive-search");

	const $menuTriggers = $module.getElementsByClassName("c-nav-trigger");

	const $promptMessage = document.querySelector(".prompt-message");

	const predictionTemplate = underscoreTemplateGrindex(document.getElementById("predictive-result-tmpl").innerHTML);
	const predictionViewAllButtonTemplate = underscoreTemplateGrindex(document.getElementById("predictive-result-view-all").innerHTML);

	let $predictiveSearchTimer = null;

	resizeHeader();

	bindEvents();

	function bindEvents() {
		window.addEventListener("scroll", () => debounce(togglePersistentHeader, 300));

		addListener(document.body, "click", removeSearchOnClickOutside);

		addListener(window, "resize", () => debounce(resizeHeader, 100));

		addListener($searchClearBtn, "click", clearSearchForm);
		addListener(document.querySelector(".header-overlay"), "click", deactivateMobileSearchAndMenu);

		addListener($module.querySelector(".country-selector-trigger"), "click", toggleMobileMenu);
		addListener($module.querySelectorAll(".search-trigger"), "click", toggleSearchField);
		addListener($module.querySelectorAll(".mobile-links .c-nav-trigger"), "click", toggleMobileMenu);
		addListener($module.querySelectorAll(".js-ss-untrigger"), "click", clearSessionStorage);

		addListener($module.querySelectorAll("input.query-field"), "keyup", queryKeyup);

		addListener($module.querySelector(".brand-search-dropdown"), "change", queryKeyup);

		addListener($module.querySelector("li.c-predictive-search form"), "click", submitForm);

		if (!isMobileView()) {
			addListener(window, "load", onPageLoad.bind(this));
		}
	}

	function onPageLoad() {
		displaySearchField($mainSearchContainer);
	}

	function removeSearchOnClickOutside(e) {
		if (!e.target.classList.contains("search-trigger") && !e.target.closest(".c-predictive-search")) {
			if (isMobileView()) {
				removeActiveSearchFields();
			}
			removePredictions();
		}
	}

	function submitForm(e) {
		let inputField = e.target.querySelector("input[type='text']");
		if (!inputField) {
			inputField = e.target;
		}
		if (inputField && inputField.value === "") {
			e.preventDefault();
		}
	}

	function togglePersistentHeader() {
		const scrollPosition = document.documentElement.scrollTop;
		if (scrollPosition > SCROLL_LIMIT) {
			document.body.classList.add("persist-hdr");
		} else {
			document.body.classList.remove("persist-hdr");
		}

		if (window.innerWidth > HDR_ACTIVATION_POINT) {
			removePredictions();
		}
	}

	function deactivateMobileSearchAndMenu(e) {
		deactivateSearchField(e);
		toggleMobileMenu(e);
	}

	function toggleMobileMenu(e) {
		if (e.target.classList.contains("icon-close-lg") || e.target.classList.contains("header-overlay")) {
			if (
				typeof e === typeof "undefined" ||
				e.target.classList.contains(CLASS_ICON_MENU_TRIGGER) ||
				e.target.classList.contains("c-nav__navigation") ||
				(e.target.closest("nav") && e.target.closest("nav").classList.contains("c-nav__navigation"))
			) {
				return;
			}

			closeMobileNavigation();
			toggleBodyScrollLock();
			toggleMobileMenuButton(true);
		} else {
			if ($module.classList.contains("search-open")) {
				deactivateSearchField(e);
			}
			openMobileNavigation();
			toggleBodyScrollLock();
			toggleMobileMenuButton(false);
		}
	}

	function toggleMobileMenuButton(setToOpened) {
		$menuTriggers.forEach(($trigger) => {
			if (setToOpened) {
				$trigger.classList.remove(CLASS_ICON_CLOSE);
				$trigger.classList.add(CLASS_ICON_MENU_TRIGGER);
			} else {
				$trigger.classList.add(CLASS_ICON_CLOSE);
				$trigger.classList.remove(CLASS_ICON_MENU_TRIGGER);
			}
		});
	}

	function queryKeyup(e) {
		e.preventDefault();

		if (verifyKey(e)) {
			return true;
		}

		const $attachedElement = e.currentTarget;
		const $triggerElement = e.target;

		if ($attachedElement.value && ($attachedElement.value === 0 || $attachedElement.value.length < 2)) {
			removePredictions();
		} else {
			if ($predictiveSearchTimer !== null) {
				clearTimeout($predictiveSearchTimer);
			}

			$predictiveSearchTimer = setTimeout(function () {
				predictiveSearch($attachedElement.value, $attachedElement);
			}, 500);

			if ($triggerElement.closest(".persistent-header") && !$triggerElement.closest(".mobile")) {
				$predictions.style.top =
					offset($triggerElement.closest(".c-predictive-search")).top +
					height($attachedElement.closest(".persistent-header")) -
					(height($promptMessage) ? height($promptMessage) : 0) +
					"px";
			} else if ($triggerElement.closest(".persistent-header") || $triggerElement.closest(".mobile")) {
				$predictions.style.top =
					offset($attachedElement.closest(".c-predictive-search")).top +
					height($attachedElement.closest(".c-predictive-search")) -
					(height($module.querySelector(".prompt-message")) ? height($module.querySelector(".prompt-message")) : 0) +
					"px";
			} else {
				$predictions.style.top = "";
			}
		}
		return false;
	}

	function clearSessionStorage() {
		sessionStorage.removeItem(xylemStorage.applicationDetail.storageName);
	}

	function verifyKey(e) {
		const key = e.keyCode;
		var $current = e.currentTarget;
		const $listItems = $predictions.querySelectorAll("a");
		let $selected = null;
		if ($listItems.length) {
			$selected = [...$listItems].filter(($item) => {
				return $item.classList.contains("selected");
			})[0];
		}

		// TODO(JS): Replace with Key, codes are deprecated
		if (key === keyCodes.enter) {
			// Enter on a result item
			if ($selected) {
				$selected.click();
				return true;
			}

			// Enter in search field
			return true;
		}

		if (key !== keyCodes.up && key !== keyCodes.down) {
			return false;
		}

		$listItems.forEach(($item) => {
			$item.classList.remove("selected");
		});

		if (key === keyCodes.down) {
			$current = getNextPrediction($selected, $listItems);
		} else if (key === keyCodes.up) {
			return getPreviousPrediction($selected, $listItems);
		}

		if ($current) {
			$current.classList.add("selected");
			e.currentTarget.value = $current.dataset.value;
			return true;
		}
		return false;
	}

	function getNextPrediction($selected, $listItems) {
		const nextSibling = getNextSibling($selected, "a");
		if (!$selected || !nextSibling) {
			return $listItems[0];
		} else {
			return nextSibling;
		}
	}

	function getPreviousPrediction($selected, $listItems) {
		const previousSibling = getPreviousSibling($selected, "a");
		if (!$selected || !previousSibling) {
			return $listItems[$listItems.length - 1];
		} else {
			return previousSibling;
		}
	}

	function toggleBodyScrollLock() {
		const shouldLock =
			(isMobileView() && ($module.classList.contains("search-open") || $module.classList.contains("menu-open"))) ||
			document.body.querySelectorAll(".blocker").length > 0;

		if (shouldLock) {
			document.body.classList.add("scroll-lock");
		} else {
			document.body.classList.remove("scroll-lock");
		}
	}

	function displaySearchField(searchContainer) {
		if (isMobileView() && $module.classList.contains("menu-open")) {
			$module.querySelector(".mobile-links .c-nav-trigger").click();
		}
		// Simulate a keyup in the search field to activate the predictions
		searchContainer.querySelectorAll("input.query-field").forEach(($input) => {
			$input.dispatchEvent(new KeyboardEvent("keypress", { key: keyCodes.up }));
		});
		searchContainer.classList.add("wide");
		$module.classList.add("search-open");
		toggleBodyScrollLock();
		$searchTriggers.forEach(($trigger) => {
			$trigger.classList.add(CLASS_ICON_CLOSE);
			$trigger.classList.remove(CLASS_ICON_SEARCH_TRIGGER);
		});
	}

	function hideSearchField(searchContainer) {
		if (searchContainer) {
			searchContainer.classList.remove("wide");
		}

		$module.classList.remove("search-open");
		toggleBodyScrollLock();
		$searchTriggers.forEach(($trigger) => {
			$trigger.classList.remove(CLASS_ICON_CLOSE);
			$trigger.classList.add(CLASS_ICON_SEARCH_TRIGGER);
		});
	}

	function toggleSearchField(e) {
		e.preventDefault();

		if (e.target.classList.contains(CLASS_ICON_CLOSE)) {
			deactivateSearchField(e);
		} else {
			activateSearchField(e);
		}
	}

	function activateSearchField(e) {
		e.preventDefault();
		if (
			typeof e === typeof "undefined" ||
			e.target.classList.contains(CLASS_ICON_CLOSE) ||
			e.target.classList.contains("clear-btn") ||
			e.target.classList.contains("query-field")
		) {
			return;
		}

		if (!isMobileView()) {
			onPageLoad();
		} else {
			displaySearchField($mainSearchContainer);
		}

		$searchClearBtn.forEach(($element) => {
			$element.classList.remove("hidden");
		});
	}

	function deactivateSearchField(e) {
		clearPredictions(e);
		if (
			typeof e === typeof "undefined" ||
			e.target.classList.contains(CLASS_ICON_SEARCH_TRIGGER) ||
			e.target.classList.contains("c-predictive-search") ||
			(e.target.closest("li") && e.target.closest("li").classList.contains("c-predictive-search"))
		) {
			return;
		}

		removeActiveSearchFields();
	}

	function removeActiveSearchFields() {
		if ($predictiveSearchTimer !== null) {
			clearTimeout($predictiveSearchTimer);
		}

		hideSearchField($mainSearchContainer);

		$searchClearBtn.forEach(($element) => {
			$element.classList.add("hidden");
		});
	}

	function submitSearchForm() {
		$searchForm.submit();
	}

	function clearSearchForm() {
		let inputField = $module.querySelector("li.c-predictive-search form input[type='text']");
		if (inputField && inputField.value !== "") {
			inputField.value = "";
		}
		removePredictions();
	}

	function predictiveSearch(term, $input) {
		const brandInput = $input.closest("form").querySelector(".brand-search-dropdown select").value;
		const brand = parseInt(brandInput);
		const isMobile = isMobileView() ? true : false;
		const url = new URL(`${window.location.origin}/${apiConfig.apiLanguage}/api/predictivesearch/suggest/`);
		const params = { query: term, category: brand, isMobile: isMobile };

		Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]));

		fetch(url)
			.then((response) => response.json())
			.then((res) => {
				if (res.total === 0) {
					removePredictions();
				} else {
					renderResults(term, $input, res.items);
				}
			})
			.catch((err) => {
				throw new Error("api search error, response: " + err);
			});
	}

	function renderResults(term, $input, results) {
		if (!isMobileView() && !$input.closest(".c-predictive-search").classList.contains("wide")) {
			return;
		}

		const $parent = $predictions.parentElement;

		$predictions.innerHTML = "";
		$predictions.innerHTML += ELEMENT_PREDICTION_TRIANGLE;

		results.forEach((item) => {
			$predictions.innerHTML += predictionTemplate({ ...item });
		}, this);

		$predictions.innerHTML += predictionViewAllButtonTemplate();
		$predictions.querySelector(".js-view-all").dataset.value = term;
		addListener($module.querySelector("a.js-view-all"), "click", submitSearchForm);

		$parent.classList.remove("hidden");

		setTimeout(function () {
			$parent.style.maxHeight = "730px";
		}, 30);
	}

	function clearPredictions(e) {
		const $target = e.target;
		const $closestLi = $target.closest("li");
		const $closestPredictiveSearch = $target.closest(".predictive-search-container");

		if (
			typeof e === typeof "undefined" ||
			$target.classList.contains("c-predictive-search") ||
			($closestLi && $closestLi.classList.contains("c-predictive-search")) ||
			$closestPredictiveSearch
		) {
			return;
		}

		removePredictions();
	}

	function removePredictions() {
		if ($predictions) {
			$predictions.innerHTML = "";
			$predictions.parentElement.classList.add("hidden");
			$predictions.parentElement.style.maxHeight = "0px";
		}
	}

	function adjustToPromptMessage() {
		if (!$promptMessage) {
			return;
		}

		if ($promptMessage.classList.contains("js-message-opened")) {
			// Prompt needs to be fixed for this header
			if (!$promptMessage.classList.contains("fixed-top")) {
				// Push down header and content on first init
				$module.style.marginTop = "0";
			} else {
				$module.style.marginTop = height($promptMessage) + "px";
			}
		}
	}

	function resizeHeader() {
		toggleBodyScrollLock();
		if (window.innerWidth > HDR_ACTIVATION_POINT) {
			$module.classList.remove("mobile");
			$module.classList.add("not-mobile");

			// Added since changes to header did not take effect on Evoqua pages.
			// Should be investigated since this is an unnecessary dom traversing
			const $header = document.querySelector(".component-header");
			$header.classList.remove("mobile");
			$header.classList.add("not-mobile");

			if (isMobileView()) {
				document.body.classList.remove("scrolled-header");
				document.html.classList.remove("scrolled-header");
			}
		} else {
			$module.classList.add("mobile");
			$module.classList.remove("not-mobile");
			if (!isMobileView()) {
				document.body.classList.add("scrolled-header");
				document.html.classList.add("scrolled-header");
			}
			if ($promptMessage && $promptMessage.classList.contains("js-message-opened")) {
				const $header = $module.querySelector(".header-overlay");
				if ($header) {
					$header.style.top = height($promptMessage) + "px";
				}
			}
		}
		adjustToPromptMessage();
	}

	function offset($element) {
		if (!$element) {
			return 0;
		}

		const rect = $element.getBoundingClientRect();
		return {
			top: rect.top + document.documentElement.scrollTop,
			left: rect.left + document.documentElement.scrollLeft,
		};
	}

	function isMobileView() {
		return $module.classList.contains("mobile");
	}
}

export default {
	view: "header",
	init,
};
